import { useState} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { toast } from "react-toastify";

// APIs & Reducers
import { useCreateCustomerMutation } from "../../api/customer.api";
import { useCreateOrderMutation } from "../../api/order.api";
import { useApplyPromoMutation } from "../../api/promo.api";
import { setCustomerId } from "../../reducer/customer.reducer";
import { clearCart } from "../../reducer/cart.reducer";
import { useInitiatePaymentMutation } from "../../api/payment.api";
import { Controller } from "react-hook-form";

// Components
import { TextInputComponent } from "../../common/Form/Input.component";
import OrderView from "../OrderView/OrderView";
import OrderDetails from "../../common/OrderDetails/OrderDetails";
import "./UnifiedCheckout.css";

const UnifiedCheckout = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [promoCode, setPromoCode] = useState("");
    const [discount, setDiscount] = useState(0);
    const [orderDetails, setOrderDetails] = useState(null);
    const [isOrderModalActive, setIsOrderModalActive] = useState(false);

    // Redux State
    const cart = useSelector((state) => state.cart);
    const cartList = cart?.items || [];
    const totalAmount = cart?.totalAmount || 0;
    const cartId = cart?._id;
    const existingCustomerId = useSelector((state) => state.customer.customerId);

    // Mutations
    const [createCustomer] = useCreateCustomerMutation();
    const [createOrder] = useCreateOrderMutation();
    const [applyPromo] = useApplyPromoMutation();
    const [initiatePayment] = useInitiatePaymentMutation();

    // Combined Schema
    const checkoutSchema = Yup.object({
        fullname: Yup.string().min(2, "Name too short").max(50).required("Full name is required"),
        email: Yup.string().email("Invalid email").nullable().notRequired(),
        phone: Yup.string()
            .matches(/^\+?[1-9]\d{1,14}$/, "Invalid phone number")
            .required("Phone number is required"),
        optionalNumber: Yup.string()
            .nullable()
            .notRequired()
            .test("phone-format", "Invalid phone number", (val) => !val || /^\+?[1-9]\d{1,14}$/.test(val)),
        country: Yup.string().required("Country is required"),
        address: Yup.string().required("Address is required"),
        landMark: Yup.string().nullable().notRequired(),
        paymentType: Yup.object({
            label: Yup.string().required(),
            value: Yup.string().required("Please select a payment method"),
        }).required("Payment method is required"),
    });

    const {
        control,
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm({
        resolver: yupResolver(checkoutSchema),
        defaultValues: {
            country: "Nepal",
        },
    });

    const handlePromoApply = async () => {
        if (!promoCode) return toast.warning("Please enter a promo code");
        try {
            const response = await applyPromo({ promoCode }).unwrap();
            setDiscount(response?.discount || 0);
            toast.success("Promo code applied!");
        } catch (err) {
            toast.error(err?.data?.message || "Invalid or expired promo code");
            setDiscount(0);
        }
    };

    const serviceCharge = totalAmount >= 10000 ? 0 : 100;
    const discountAmount = (discount / 100) * totalAmount;
    const grandTotal = totalAmount - discountAmount + serviceCharge;

    const onPlaceOrder = async (data) => {
        setLoading(true);
        try {
            let customerId = existingCustomerId;
            // 1. Create or Update Customer
            const customerData = {
                fullname: data.fullname,
                email: data.email,
                phone: data.phone,
                optionalNumber: data.optionalNumber,
                country: data.country,
                address: data.address,
                landMark: data.landMark,
            };

            const custResponse = await createCustomer(customerData).unwrap();
            customerId = custResponse?.result?._id;
            dispatch(setCustomerId(customerId));

            // 2. Create Order
            const orderData = {
                items: cartList,
                paymentType: data.paymentType.value,
                customerId: customerId,
                cartId: cartId,
                promoCode,
                subTotal: totalAmount,
                cartTotal: totalAmount,
            };

            const orderResponse = await createOrder(orderData).unwrap();
            
            // 3. Handle Payment Redirection or Success
            if (data.paymentType.value === "Esewa") {
                const paymentParams = await initiatePayment({
                    amount: grandTotal,
                    orderId: orderResponse.details.id, // Firestore Doc ID
                }).unwrap();
                
                // Redirect to eSewa
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = paymentParams.esewaUrl;

                const fields = {
                    amount: paymentParams.amount,
                    tax_amount: paymentParams.taxAmount,
                    total_amount: paymentParams.totalAmount,
                    transaction_uuid: paymentParams.transactionUUID,
                    product_code: paymentParams.productCode,
                    product_service_charge: 0,
                    product_delivery_charge: 0,
                    success_url: paymentParams.successUrl,
                    failure_url: paymentParams.failureUrl,
                    signed_field_names: 'total_amount,transaction_uuid,product_code',
                    signature: paymentParams.signature,
                };

                for (const key in fields) {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = key;
                    input.value = fields[key];
                    form.appendChild(input);
                }

                document.body.appendChild(form);                
                // Cleanup Redux before leaving the page
                dispatch(setCustomerId(null));
                dispatch(clearCart());
                
                form.submit();
            } else {
                setOrderDetails(orderResponse?.details);
                setIsOrderModalActive(true);
                // Cleanup Redux
                dispatch(setCustomerId(null));
                dispatch(clearCart());
            }
        } catch (err) {
            console.error("Checkout Error:", err);
            toast.error(err?.data?.message || "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (cartList.length === 0 && !isOrderModalActive) {
        return (
            <div className="unified_checkout_container" style={{ textAlign: "center", padding: "100px" }}>
                <h2>Your cart is empty</h2>
                <button 
                    onClick={() => navigate("/all_product")}
                    className="promo_apply_btn" 
                    style={{ marginTop: "20px" }}
                >
                    Continue Shopping
                </button>
            </div>
        );
    }

    return (
        <div className="unified_checkout_container">
            <div className="checkout_grid">
                {/* Left Side: Forms */}
                <form id="checkout-form" onSubmit={handleSubmit(onPlaceOrder)} className="checkout_main">
                    <section className="checkout_section">
                        <h2 className="section_title"><span>1</span> Shipping Information</h2>
                        <div className="form_group">
                            <label>Full Name*</label>
                            <TextInputComponent
                                name="fullname"
                                control={control}
                                errMsg={errors?.fullname?.message}
                                placeholder="Enter your full name"
                            />
                        </div>
                        <div className="form_group_row">
                            <div className="form_group">
                                <label>Email Address</label>
                                <TextInputComponent
                                    name="email"
                                    type="email"
                                    control={control}
                                    errMsg={errors?.email?.message}
                                    placeholder="your@email.com"
                                />
                            </div>
                            <div className="form_group">
                                <label>Phone Number*</label>
                                <TextInputComponent
                                    name="phone"
                                    control={control}
                                    errMsg={errors?.phone?.message}
                                    placeholder="98XXXXXXXX"
                                />
                            </div>
                        </div>
                        <div className="form_group">
                            <label>Optional Number</label>
                            <TextInputComponent
                                name="optionalNumber"
                                control={control}
                                errMsg={errors?.optionalNumber?.message}
                                placeholder="Alternative contact"
                            />
                        </div>
                    </section>

                    <section className="checkout_section">
                        <h2 className="section_title"><span>2</span> Delivery Details</h2>
                        <div className="form_group_row">
                            <div className="form_group">
                                <label>Country*</label>
                                <TextInputComponent
                                    name="country"
                                    control={control}
                                    defaultValue="Nepal"
                                    errMsg={errors?.country?.message}
                                />
                            </div>
                            <div className="form_group">
                                <label>Address*</label>
                                <TextInputComponent
                                    name="address"
                                    control={control}
                                    errMsg={errors?.address?.message}
                                    placeholder="Street, City, Area"
                                />
                            </div>
                        </div>
                        <div className="form_group">
                            <label>Nearest Landmark</label>
                            <TextInputComponent
                                name="landMark"
                                control={control}
                                errMsg={errors?.landMark?.message}
                                placeholder="E.g. Near City Center"
                            />
                        </div>
                    </section>

                    <section className="checkout_section">
                        <h2 className="section_title"><span>3</span> Payment Method</h2>
                        <Controller
                            name="paymentType"
                            control={control}
                            render={({ field }) => (
                                <div className="payment_methods_grid">
                                    <button
                                        type="button"
                                        className={`payment_method_card ${field.value?.value === 'Cash on delivery' ? 'active' : ''}`}
                                        onClick={() => field.onChange({ label: 'Cash on delivery', value: 'Cash on delivery' })}
                                    >
                                        <div className="method_icon">💵</div>
                                        <div className="method_info">
                                            <span className="method_name">Cash on Delivery</span>
                                            <span className="method_desc">Pay when you receive</span>
                                        </div>
                                    </button>
                                    {/* <button
                                        type="button"
                                        className={`payment_method_card ${field.value?.value === 'Esewa' ? 'active' : ''}`}
                                        onClick={() => field.onChange({ label: 'Pay by E-sewa', value: 'Esewa' })}
                                    >
                                        <div className="method_icon">💳</div>
                                        <div className="method_info">
                                            <span className="method_name">eSewa</span>
                                            <span className="method_desc">Pay securely via eSewa</span>
                                        </div>
                                    </button> */}
                                </div>
                            )}
                        />
                        {errors.paymentType?.value && (
                            <span className="error_message" style={{color: 'red', fontSize: '0.8em', marginTop: '10px', display: 'block'}}>
                                {errors.paymentType.value.message}
                            </span>
                        )}
                    </section>
                </form>

                {/* Right Side: Summary Card */}
                <aside className="checkout_sidebar">
                    <div className="summary_card">
                        <h2 className="summary_title">Order Summary ({cartList.length})</h2>
                        
                        <div className="order_items_list">
                            {cartList.map((item, idx) => (
                                <OrderView
                                    key={idx}
                                    title={item.title}
                                    quantity={item.quantity}
                                    price={item.amount || (item.price * item.quantity)}
                                    image={item.productImage}
                                    size={item.size}
                                />
                            ))}
                        </div>

                        <div className="promo_section">
                            <input 
                                type="text" 
                                placeholder="Promo code" 
                                className="promo_input"
                                value={promoCode}
                                onChange={(e) => setPromoCode(e.target.value)}
                            />
                            <button type="button" onClick={handlePromoApply} className="promo_apply">Apply</button>
                        </div>

                        <div className="totals_section">
                            <div className="total_row">
                                <span>Subtotal</span>
                                <span>Rs. {totalAmount.toLocaleString()}</span>
                            </div>
                            <div className="total_row">
                                <span>Service Charge</span>
                                <span>Rs. {serviceCharge}</span>
                            </div>
                            {discount > 0 && (
                                <div className="total_row" style={{ color: "#27ae60" }}>
                                    <span>Discount ({discount}%)</span>
                                    <span>- Rs. {discountAmount.toLocaleString()}</span>
                                </div>
                            )}
                            <div className="total_row grand_total">
                                <span>Total</span>
                                <span>Rs. {grandTotal.toLocaleString()}</span>
                            </div>
                        </div>

                        <button 
                            type="submit" 
                            form="checkout-form"
                            disabled={loading} 
                            className="place_order_btn"
                        >
                            {loading ? (
                                <div className="loading_spinner"></div>
                            ) : (
                                <>Place Order <span style={{fontSize: '1.2em'}}>→</span></>
                            )}
                        </button>
                    </div>
                </aside>
            </div>

            <OrderDetails
                isActive={isOrderModalActive}
                toggleOrderDetails={() => setIsOrderModalActive(!isOrderModalActive)}
                orderDetails={orderDetails}
            />
        </div>
    );
};

export default UnifiedCheckout;
